// pages/TarifsPage.jsx
// Page de présentation des tarifs et abonnements

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X, CreditCard } from 'lucide-react';
import './TarifsPage.css';

export default function TarifsPage() {
  const { t } = useTranslation();
  // État pour le toggle mensuel/annuel
  const [isYearly, setIsYearly] = useState(false);

  // Données des niveaux d'abonnement
  const plans = [
    {
      id: 'gratuit',
      title: t('profile.niveauGratuit'),
      priceMonthly: '0€',
      priceYearly: '0€',
      badge: t('tarifs.badgeGratuit'),
      features: [
        { text: t('tarifs.featGratuitExercices'), included: true },
        { text: t('tarifs.featGratuitPlaylist'), included: true },
        { text: t('tarifs.featGratuitMots'), included: true },
        { text: t('tarifs.featGratuitBase'), included: true },
        { text: t('tarifs.featCorrections5'), included: true },
        { text: t('tarifs.featCorrectionsLimit20'), included: true },
        { text: t('tarifs.featPlaylistsPubliques20'), included: true },
        { text: t('tarifs.featStatistiques'), included: false },
        { text: t('tarifs.featSupportPrioritaire'), included: false },
        { text: t('tarifs.featAccesAnticipe'), included: false },
      ],
      cta: t('tarifs.ctaGratuit'),
      highlighted: false,
    },
    {
      id: 'basique',
      title: t('profile.niveauBasique'),
      priceMonthly: '5€',
      priceYearly: '50€',
      badge: t('tarifs.badgeBasique'),
      yearlySave: t('tarifs.economise', { montant: '10€' }),
      features: [
        { text: t('tarifs.featBasiqueExercices'), included: true },
        { text: t('tarifs.featBasiquePlaylists'), included: true },
        { text: t('tarifs.featBasiqueMots'), included: true },
        { text: t('tarifs.featToutesPlaylistsPubliques'), included: true },
        { text: t('tarifs.featCorrections5ParJour'), included: true },
        { text: t('tarifs.featCorrectionsLimit200'), included: true },
        { text: t('tarifs.featStatistiques'), included: true },
        { text: t('tarifs.featSupportPrioritaire'), included: false },
        { text: t('tarifs.featAccesAnticipe'), included: false },
      ],
      cta: t('tarifs.ctaBasique'),
      highlighted: true,
    },
    {
      id: 'premium',
      title: t('profile.niveauPremium'),
      priceMonthly: '10€',
      priceYearly: '100€',
      badge: t('tarifs.badgePremium'),
      yearlySave: t('tarifs.economise', { montant: '20€' }),
      features: [
        { text: t('tarifs.featExercicesIllimites'), included: true },
        { text: t('tarifs.featPremiumPlaylists'), included: true },
        { text: t('tarifs.featPremiumMots'), included: true },
        { text: t('tarifs.featAccesCompletPlaylists'), included: true },
        { text: t('tarifs.featCorrections15ParJour'), included: true },
        { text: t('tarifs.featCorrectionsLimit200'), included: true },
        { text: t('tarifs.featStatistiques'), included: true },
        { text: t('tarifs.featSupportPrioritaire'), included: true },
        { text: t('tarifs.featAccesAnticipe'), included: true },
      ],
      cta: t('tarifs.ctaPremium'),
      highlighted: true,
    },
  ];

  return (
    <div className="tarifs-page">
      <div className="tarifs-container">
        <header className="tarifs-header">
          <h1 className="tarifs-title">{t('tarifs.title')}</h1>
          <p className="tarifs-subtitle">{t('tarifs.subtitle')}</p>
        </header>

        <div className="pricing-toggle">
          <span className="toggle-label">{t('tarifs.tarification')}</span>
          <div className="toggle-options">
            <span className={`toggle-option ${!isYearly ? 'active' : ''}`} onClick={() => setIsYearly(false)}>
              {t('tarifs.mensuelle')}
            </span>
            <span className={`toggle-option ${isYearly ? 'active' : ''}`} onClick={() => setIsYearly(true)}>
              {t('tarifs.annuelle')}
            </span>
          </div>
        </div>

        <div className="plans-grid">
          {plans.map((plan) => (
            <div key={plan.id} className={`plan-card ${plan.highlighted ? 'highlighted' : ''}`}>
              <div className="plan-header">
                <h3 className="plan-title">{plan.title}</h3>
                <span className="plan-badge">{plan.badge}</span>
              </div>

              <div className="plan-pricing">
                <span className="plan-price">{isYearly ? plan.priceYearly : plan.priceMonthly}</span>
                <span className="plan-period">{isYearly ? t('tarifs.perYear') : t('tarifs.perMonth')}</span>
                {plan.yearlySave && !isYearly && <span className="plan-save">{t('tarifs.ouParAn', { prix: plan.priceYearly })}</span>}
              </div>

              <ul className="plan-features">
                {plan.features.map((feature, index) => (
                  <li key={index} className={`feature-item ${feature.included ? 'included' : 'excluded'}`}>
                    {feature.included ? <Check size={16} color="#10B981" /> : <X size={16} color="#EF4444" />}
                    <span>{feature.text}</span>
                  </li>
                ))}
              </ul>

              <button className="plan-cta">{plan.cta}</button>

              {plan.id !== 'gratuit' && (
                <div className="payment-methods">
                  <CreditCard size={16} />
                  <span>{t('tarifs.paiementSecurise')}</span>
                </div>
              )}
            </div>
          ))}
        </div>

        <section className="faq-section">
          <h2 className="faq-title">{t('tarifs.faqTitle')}</h2>

          <div className="faq-grid">
            <div className="faq-item">
              <h4>{t('tarifs.faq1Q')}</h4>
              <p>{t('tarifs.faq1A')}</p>
            </div>

            <div className="faq-item">
              <h4>{t('tarifs.faq2Q')}</h4>
              <p>{t('tarifs.faq2A')}</p>
            </div>

            <div className="faq-item">
              <h4>{t('tarifs.faq3Q')}</h4>
              <p>{t('tarifs.faq3A')}</p>
            </div>

            <div className="faq-item">
              <h4>{t('tarifs.faq4Q')}</h4>
              <p>{t('tarifs.faq4A')}</p>
            </div>
          </div>
        </section>

        <section className="comparison-section">
          <h2 className="comparison-title">{t('tarifs.comparisonTitle')}</h2>

          <div className="comparison-table">
            <div className="comparison-header">
              <div className="comparison-cell"></div>
              <div className="comparison-cell">{t('profile.niveauGratuit')}</div>
              <div className="comparison-cell">{t('profile.niveauBasique')}</div>
              <div className="comparison-cell">{t('profile.niveauPremium')}</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell feature-name">{t('tarifs.compExercicesJour')}</div>
              <div className="comparison-cell">10</div>
              <div className="comparison-cell">50</div>
              <div className="comparison-cell">{t('tarifs.compIllimite')}</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell feature-name">{t('tarifs.compPlaylistsPerso')}</div>
              <div className="comparison-cell">1</div>
              <div className="comparison-cell">5</div>
              <div className="comparison-cell">20</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell feature-name">{t('tarifs.compMotsPlaylist')}</div>
              <div className="comparison-cell">20</div>
              <div className="comparison-cell">50</div>
              <div className="comparison-cell">200</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell feature-name">{t('tarifs.compPlaylistsPubliques')}</div>
              <div className="comparison-cell">❌</div>
              <div className="comparison-cell">✅</div>
              <div className="comparison-cell">✅</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell feature-name">{t('tarifs.compEnrichissementIA')}</div>
              <div className="comparison-cell">❌</div>
              <div className="comparison-cell">{t('tarifs.compLimite')}</div>
              <div className="comparison-cell">{t('tarifs.compIllimite')}</div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell feature-name">{t('tarifs.featStatistiques')}</div>
              <div className="comparison-cell">❌</div>
              <div className="comparison-cell">{t('profile.niveauBasique')}</div>
              <div className="comparison-cell">{t('tarifs.compAvancees')}</div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
